import { useParams, Link } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  CreditCard,
  User,
  Building2,
  FileText,
  Shield,
  Calendar,
  Phone,
  Mail,
  MapPin,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { FioriStatusBadge } from "@/components/fiori";

function DetailRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-1 py-2.5 border-b border-[#F0F0F0] last:border-0">
      <span className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wide sm:w-44 shrink-0">{label}</span>
      <span className="text-sm text-[#2C2C2C] font-medium">{value || "—"}</span>
    </div>
  );
}

function SectionCard({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-[#E0E0E0] rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 bg-[#FAFAFA] border-b border-[#E0E0E0]">
        <div className="text-[#5B2C93]">{icon}</div>
        <h3 className="text-sm font-semibold text-[#2C2C2C]">{title}</h3>
      </div>
      <div className="px-4 py-1">{children}</div>
    </div>
  );
}

export default function McmRequestDetail() {
  const params = useParams<{ id: string }>();
  const id = parseInt(params.id || "0");

  const { data: request, isLoading, error } = trpc.mcm.requests.getById.useQuery(
    { id },
    { enabled: !!id }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[#5B2C93]" />
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-3">
        <AlertTriangle className="h-10 w-10 text-[#DC2626]" />
        <p className="text-sm text-[#6B6B6B]">Request not found or failed to load.</p>
        <Link href="/mcm">
          <Button variant="outline" size="sm">Back to Card Directory</Button>
        </Link>
      </div>
    );
  }

  const fd = (request.formData as any) || {};
  const enriched = (request as any)._enriched || {};
  const accessLevel = enriched; // names resolved server-side

  const companyTypeColors: Record<string, { bg: string; text: string }> = {
    permit3: { bg: "#E8DCF5", text: "#5B2C93" },
    internal: { bg: "#DBEAFE", text: "#2563EB" },
    contractor: { bg: "#FEF3C7", text: "#D97706" },
    subcontractor: { bg: "#E8DCF5", text: "#5B2C93" },
    client: { bg: "#D1FAE5", text: "#059669" },
  };
  const ctStyle = companyTypeColors[fd.companyType] || { bg: "#F5F5F5", text: "#6B6B6B" };

  return (
    <div className="-mx-4 md:-mx-6 -mb-4 md:-mb-6 min-h-[calc(100vh-4rem)] bg-white px-4 md:px-6">
      {/* Page Header */}
      <div className="flex items-center gap-3 px-1 pb-4 border-b border-[#E0E0E0] mb-5">
        <Link href="/mcm">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#6B6B6B] hover:text-[#2C2C2C]">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="h-8 w-8 rounded-lg bg-[#E8DCF5] flex items-center justify-center">
          <CreditCard className="h-4 w-4 text-[#5B2C93]" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-semibold text-[#2C2C2C] truncate">
            {request.requestNumber || `MCM-REQ-${request.id}`}
          </h1>
          <p className="text-xs text-[#6B6B6B]">Card Request Detail</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span
            className="px-2 py-0.5 rounded text-xs font-medium uppercase"
            style={{ backgroundColor: ctStyle.bg, color: ctStyle.text }}
          >
            {fd.companyType || "—"}
          </span>
          <FioriStatusBadge
            status={
              request.status === "approved" ? "success"
              : request.status === "rejected" ? "error"
              : request.status === "pending" ? "pending"
              : "inactive"
            }
            label={request.status || "pending"}
          />
        </div>
      </div>

      {/* Meta row */}
      <div className="flex flex-wrap gap-4 text-xs text-[#6B6B6B] mb-5">
        <span className="flex items-center gap-1">
          <Calendar className="h-3.5 w-3.5" />
          Submitted: {request.createdAt ? new Date(request.createdAt).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }) : "—"}
        </span>
        <span className="flex items-center gap-1">
          <FileText className="h-3.5 w-3.5" />
          Type: {request.operationType?.replace(/_/g, " ") || "New Card"}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Company Information */}
        <SectionCard icon={<Building2 className="h-4 w-4" />} title="Company Information">
          <DetailRow label="Company Type" value={fd.companyType} />
          <DetailRow label="Company" value={enriched.companyName} />
        </SectionCard>

        {/* Personal Information */}
        <SectionCard icon={<User className="h-4 w-4" />} title="Personal Information">
          <DetailRow label="Full Name" value={fd.fullName} />
          <DetailRow label="Full Name (Arabic)" value={fd.fullNameAr} />
          <DetailRow label="ID Type" value={fd.idType?.replace(/_/g, " ").toUpperCase()} />
          <DetailRow label="ID Number" value={fd.idNumber} />
          <DetailRow label="Nationality" value={fd.nationality} />
          <DetailRow label="Date of Birth" value={fd.birthDate} />
          <DetailRow label="Gender" value={fd.gender} />
          <DetailRow label="Blood Type" value={fd.bloodType} />
          <DetailRow label="Mobile" value={fd.mobile} />
          <DetailRow label="Email" value={fd.email} />
          <DetailRow label="Profession" value={fd.profession} />
        </SectionCard>

        {/* ID Document Details */}
        <SectionCard icon={<FileText className="h-4 w-4" />} title="ID Document Details">
          <DetailRow label="Issue Date" value={fd.idIssueDate} />
          <DetailRow label="Issue Place" value={fd.idIssuePlace} />
          <DetailRow label="Expiry Date" value={fd.idExpiryDate} />
        </SectionCard>

        {/* Access Level */}
        <SectionCard icon={<Shield className="h-4 w-4" />} title="Access Level">
          <DetailRow label="Country" value={enriched.countryName} />
          <DetailRow label="Site" value={enriched.siteName} />
          <DetailRow label="Zone" value={enriched.zoneName} />
          <DetailRow label="Area" value={enriched.areaName} />
        </SectionCard>
      </div>

      {/* Documents */}
      <div className="mt-4">
        <SectionCard icon={<FileText className="h-4 w-4" />} title="Uploaded Documents">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-3">
            {/* Photo */}
            <div className="flex flex-col items-center gap-2">
              <p className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wide">Photo</p>
              {fd.photoUrl ? (
                <a href={fd.photoUrl} target="_blank" rel="noopener noreferrer">
                  <img
                    src={fd.photoUrl}
                    alt="Photo"
                    className="h-28 w-24 object-cover rounded border border-[#E0E0E0] hover:opacity-80 transition-opacity"
                  />
                </a>
              ) : (
                <div className="h-28 w-24 rounded border border-dashed border-[#E0E0E0] flex items-center justify-center text-xs text-[#B0B0B0]">
                  Not uploaded
                </div>
              )}
            </div>

            {/* ID Document */}
            <div className="flex flex-col items-center gap-2">
              <p className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wide">ID Document</p>
              {fd.idDocumentUrl ? (
                <a
                  href={fd.idDocumentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1 px-4 py-3 border border-[#5B2C93] rounded text-[#5B2C93] text-xs font-medium hover:bg-[#E8DCF5] transition-colors"
                >
                  <FileText className="h-6 w-6" />
                  View Document
                </a>
              ) : (
                <div className="h-28 w-24 rounded border border-dashed border-[#E0E0E0] flex items-center justify-center text-xs text-[#B0B0B0]">
                  Not uploaded
                </div>
              )}
            </div>

            {/* Contract */}
            <div className="flex flex-col items-center gap-2">
              <p className="text-xs font-medium text-[#6B6B6B] uppercase tracking-wide">Contract</p>
              {fd.contractUrl ? (
                <a
                  href={fd.contractUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1 px-4 py-3 border border-[#5B2C93] rounded text-[#5B2C93] text-xs font-medium hover:bg-[#E8DCF5] transition-colors"
                >
                  <FileText className="h-6 w-6" />
                  View Contract
                </a>
              ) : (
                <div className="h-28 w-24 rounded border border-dashed border-[#E0E0E0] flex items-center justify-center text-xs text-[#B0B0B0]">
                  Not uploaded
                </div>
              )}
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Admin Notes */}
      {(request as any).reviewNotes && (
        <div className="mt-4">
          <SectionCard icon={<AlertTriangle className="h-4 w-4" />} title="Admin Notes">
            <p className="text-sm text-[#2C2C2C] py-3">{(request as any).reviewNotes}</p>
          </SectionCard>
        </div>
      )}
    </div>
  );
}
